import {HttpStatusCode} from '@angular/common/http';
import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    DeletedBaseType,
    EntityType,
    ReferenceBookResponse,
    ReferenceBookType, TrendApiService,
    TrendResponse, TrendStatus,
} from '@npck/af-frontend-api';
import dayjs from 'dayjs';
import {DeleteDialogComponent, DeleteDialogData} from '../../../../components/delete-dialog/delete-dialog.component';
import {TableEvent} from '../../../../components/full-table/full-table.component';
import {Path} from '../../../../path';
import {DATE_UI_FORMAT, downloadFromBlob, LocalizationService, NESTED_TAB, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {EMPTY, mergeMap, Observable, of, Subscription, zip} from 'rxjs';
import {mapTrendStatusToStyle, StatusStyle} from '../../../../components/status/status.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {TREND_ID} from '../../../../domain/query-params.keys';
import {DisplayRowItem, MAX_LENGTH_TO_CROP} from '../../../incidents/details/info/info.component';
import {ANALYTIC_TREND_TAB, AnalyticsTab} from '../../analytics.component';

@Component({
    templateUrl: 'trend-details.component.html',
    styleUrls: ['trend-details.component.scss'],
    standalone: false,
})
export class TrendDetailsComponent implements OnDestroy {
    EntityType = EntityType;
    tabPathToTab: Record<string, AnalyticsTab> = {trends: 'tabTrends'};
    trend: TrendResponse;
    statusStyle: StatusStyle;
    trendId: string;
    info: DisplayRowItem[][] = [];
    tabs: TrendDetailsTab[] = [
        'tabTrendDetails',
        'tabTrendIncidents',
        'tabTrendStatistics',
        'tabTrendFeedbacks',
        'tabHistory',
    ];
    selectedTabIndex: number;
    showButton: boolean;
    deletedBaseName: string;
    statusName: string;
    activeTab: TrendDetailsTab;
    exportReportSubscription: Subscription;
    private deletedBaseDictionary: ReferenceBookResponse[];
    private trendTypes: ReferenceBookResponse[];
    private trendStatuses: ReferenceBookResponse[];
    private countries: ReferenceBookResponse[];
    private regions: ReferenceBookResponse[];
    private sourceTypes: ReferenceBookResponse[];
    private fraudTypes: ReferenceBookResponse[];
    private financialOrgRoles;

    constructor(
        public authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private trendApiService: TrendApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private localizationService: LocalizationService,
    ) {
        this.trendId = this.activatedRoute.snapshot.paramMap.get(TREND_ID);
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB) as TrendDetailsTab || 'tabTrendDetails';
        this.financialOrgRoles = this.authHolderService.hasAccess(['npck-admin', 'finance-user']);
        if (!this.authHolderService.hasAccess(['npck-admin'])) {
            const index = this.tabs.indexOf('tabTrendFeedbacks', 0);
            if (index > -1) {
                this.tabs.splice(index, 1);
            }
        }
        zip([
            this.trendApiService.getTrend(this.trendId),
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.TREND_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.COUNTRY),
            this.dictionaryHolderService.getReference(ReferenceBookType.REGION),
            this.dictionaryHolderService.getReference(ReferenceBookType.SOURCE_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.FRAUD_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.DELETED_BASED),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe({
                next: ([response, trendTypes, trendStatuses, countries, regions, sourceTypes, fraudTypes, deletedBaseDictionary]) => {
                    this.deletedBaseDictionary = deletedBaseDictionary;
                    this.trendTypes = trendTypes;
                    this.trendStatuses = trendStatuses;
                    this.countries = countries;
                    this.regions = regions;
                    this.sourceTypes = sourceTypes;
                    this.fraudTypes = fraudTypes;
                    this.setTrend(response);
                },
                error: error => {
                    if (error.status === HttpStatusCode.Forbidden) {
                        this.navigateBack();
                        this.snackbarService.showSnackBar('i18n_af_snackBarMessageTrendAccessRestricted', 'error', false,
                            {trendId: this.trendId});
                        return;
                    }
                    throw error;
                },
            });
    }

    get buttonLabel(): string {
        return this.authHolderService.hasAccess(['npck-admin']) ? 'i18n_af_buttonMakeTrendConclusion' : 'i18n_af_buttonRateTrend';
    }

    get reportDownloadIsAvailable(): boolean {
        if (this.authHolderService.hasAccess(['npck-admin'])) {
            return true;
        }

        return this.financialOrgRoles && !this.trend?.isDeleted && this.trend?.status === TrendStatus.CONFIRMED;
    }

    ngOnDestroy(): void {
        this.exportReportSubscription?.unsubscribe();
    }

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.routerService.navigate(Path.ANALYTICS_TREND_DETAILS, {
            pathParams: {
                [TREND_ID]: this.trendId,
            },
            queryParams: {
                [TAB]: ANALYTIC_TREND_TAB,
                [NESTED_TAB]: this.activeTab,
            },
            queryParamsHandling: 'merge',
        });
    }

    navigateBack = (): Observable<boolean> => {
        this.routerService.navigateToTab<AnalyticsTab>(Path.ANALYTICS, ANALYTIC_TREND_TAB);
        return of(false);
    };

    makeConclusionOrRateTrend(): void {
        this.routerService.navigate(this.authHolderService.hasAccess(['npck-admin'])
            ? Path.ANALYTICS_TREND_DETAILS_CONCLUSION
            : Path.ANALYTICS_TREND_DETAILS_RATE, {
            pathParams: {
                [TREND_ID]: this.trendId,
            },
            queryParams: {
                [TAB]: ANALYTIC_TREND_TAB,
            }
        });
    };

    editTrend(): void {
        this.routerService.navigate(Path.ANALYTICS_TREND_EDIT, {
            pathParams: {
                [TREND_ID]: this.trendId,
            },
            queryParams: {
                [TAB]: ANALYTIC_TREND_TAB,
                [NESTED_TAB]: this.activeTab,
            }
        });
    };

    deleteTrend(): void {
        this.dialogService.openCustomDialog<DeleteDialogComponent, {comment: string; reason: DeletedBaseType | null}>(
            DeleteDialogComponent,
            {
                id: this.trendId,
                title: 'i18n_af_deleteTrendDialogTitle',
                subtitle: 'i18n_af_deleteTrendDialogSubtitle',
                showReason: true,
                reasonFilter: ['TRENDS'],
            } as DeleteDialogData,
        )
            .pipe(
                mergeMap(result => {
                    if (!result) {
                        return EMPTY;
                    }
                    const normalized: {comment: string; reason: DeletedBaseType | null} =
                        typeof result === 'string' ? {comment: result, reason: null} : result;
                    return this.trendApiService.deleteTrend(
                        this.trendId,
                        normalized.reason,
                        normalized.comment,
                    );
                }),
            )
            .subscribe(() => {
                this.snackbarService
                    .showSnackBar('i18n_af_snackBarMessageTrendDeleted', 'success', false, {trendId: this.trendId});
                this.navigateBack();
            });
    }

    downloadReport(): void {
        const request = this.trendApiService.exportTrend(this.trendId);

        const fileName = this.localizationService.getLocalizedKey('i18n_af_report#trends');
        this.exportReportSubscription = request.subscribe({
            next: file => {
                downloadFromBlob(file, `${fileName}.xlsx`);
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageTrendReportSuccessfullyExported');
            }, error: err => {
                throw err;
            },
        });
    };

    private setTrend(response: TrendResponse): void {
        this.trend = response;
        this.selectedTabIndex = Math.max(0, this.tabs.findIndex(tab => tab === this.activeTab));
        this.showButton = this.checkHasRatingButton();
        this.statusStyle = mapTrendStatusToStyle(response.status, response.isDeleted);
        this.statusName = response.isDeleted
            ? this.localizationService.getLocalizedKey('i18n_af_statusDeleted')
            : (findValueInDictionary(this.trendStatuses, response.status, 'name_ru') ?? '-');
        if (response.isDeleted) {
            this.deletedBaseName = findValueInDictionary(this.deletedBaseDictionary, response.deletedBase, 'name_ru');
        }
        this.build(response);
    }

    private checkHasRatingButton(): boolean {
        if (this.authHolderService.hasAccess(['npck-admin']) && !this.trend?.isReadOnly) {
            return true;
        }

        if (this.financialOrgRoles && !this.trend?.isDeleted && this.trend?.status === TrendStatus.CONFIRMED && this.trend?.canBeRated) {
            return true;
        }

        return false;
    }

    private build(response: TrendResponse): void {
        const firstRow: DisplayRowItem[] = [
            {
                type: 'text',
                label: 'trendType',
                value: findValueInDictionary(this.trendTypes, response.type, 'name_ru') ?? '-',
            },
            {
                type: 'status',
                label: 'trendStatus',
                value: this.statusName,
                statusStyle: this.statusStyle,
            },
            {
                type: 'text',
                label: 'sourceType',
                value: findValueInDictionary(this.sourceTypes, response.source, 'name_ru') ?? '-',
            },
            {
                type: 'text',
                label: 'country',
                value: findValueInDictionary(this.countries, response.country, 'name_ru') ?? '-',
            },
            {
                type: 'date-time',
                label: 'trendCreatedDate',
                value: response.createdDate,
            },
        ];
        const regions = response.regions?.map(region =>
            findValueInDictionary(this.regions, region, 'name_ru') ?? '-'
        ).join(', ');
        const secondRow: DisplayRowItem[] = [
            {
                type: 'text',
                label: 'trendFraudType',
                value: findValueInDictionary(this.fraudTypes, response.fraudType, 'name_ru') ?? '-',
            },
            {
                type: 'text',
                label: 'trendFraudScheme',
                value: response.fraudScheme,
                cropText: response.fraudScheme?.length > MAX_LENGTH_TO_CROP,
            },
            {
                type: 'text',
                label: 'uniqueVictims',
                value: response.uniqueVictimCount,
            },
            {
                type: 'text',
                label: 'region',
                value: regions,
                cropText: regions.length > MAX_LENGTH_TO_CROP,
            },
            {
                type: 'text',
                label: 'trendPeriodDetected',
                value: this.formatDate(response.periodDetectedFrom) + ' - ' + this.formatDate(response.periodDetectedTo),
            },
        ];
        this.info = [firstRow.filter(val => !!val), secondRow];
    }

    private formatDate(date: string): string {
        return date ? dayjs(date).format(DATE_UI_FORMAT) : '-';
    }
}

export declare type TrendDetailsTab =
    'tabTrendDetails'
    | 'tabTrendIncidents'
    | 'tabTrendStatistics'
    | 'tabTrendFeedbacks'
    | 'tabHistory';

export interface TrendDetailsTabState {
    tableEvent: TableEvent;
}
